import { useEffect, useState } from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WealthShell } from "@/pages/viveka/wealth/WealthShell";
import { useWealthConfig } from "@/lib/wealth";
import { toast } from "sonner";

const FIELDS = [
  ["family_name", "Family name", "text"],
  ["statement_date", "Statement / review date", "date"],
  ["base_currency", "Base currency", "text"],
  ["generation", "Generation", "text"],
  ["operating_business_name", "Operating business", "text"],
  ["next_review_date", "Next review date", "date"],
];

export default function FamilyWealthProfile() {
  const { tenantId } = useAuth();
  const config = useWealthConfig();
  const [profile, setProfile] = useState(null);
  const [entities, setEntities] = useState([]);
  const [form, setForm] = useState({ name: "", entity_type: "Individual", notes: "" });

  const load = async () => {
    if (!tenantId) return;
    try {
      const [p, e] = await Promise.all([
        api.get(`/tenants/${tenantId}/wealth/profile`),
        api.get(`/tenants/${tenantId}/wealth/entities`),
      ]);
      setProfile(p.data);
      setEntities(e.data);
    } catch (err) { toast.error(errMsg(err)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  const save = async () => {
    try {
      const { can_edit, id, tenant_id, status, ...body } = profile;
      await api.put(`/tenants/${tenantId}/wealth/profile`, body);
      toast.success("Family wealth profile saved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addEntity = async () => {
    if (!form.name.trim()) return toast.error("Enter the entity name");
    try {
      await api.post(`/tenants/${tenantId}/wealth/entities`, form);
      setForm({ name: "", entity_type: "Individual", notes: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeEntity = async (id) => {
    try {
      await api.delete(`/tenants/${tenantId}/wealth/entities/${id}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!profile) return <WealthShell title="Family profile & wealth purpose"><p className="label-caps animate-pulse">Loading</p></WealthShell>;
  const canEdit = profile.can_edit;

  return (
    <WealthShell title="Family profile & wealth purpose"
                 description="Governance context for the family wealth statement: who owns what, the purpose of the wealth and the review cadence.">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.3fr_1fr]" data-testid="wealth-profile">
        <div className="panel space-y-5 p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {FIELDS.map(([key, label, type]) => (
              <label key={key} className="block">
                <span className="label-caps">{label}</span>
                <input type={type} value={profile[key] || ""} disabled={!canEdit}
                       data-testid={`wealth-profile-${key}`}
                       onChange={(e) => setProfile({ ...profile, [key]: e.target.value })}
                       className="mt-2 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-navy outline-none focus:border-plum disabled:opacity-60" />
              </label>
            ))}
            <label className="block">
              <span className="label-caps">Review frequency</span>
              <select value={profile.review_frequency || "Annual"} disabled={!canEdit}
                      data-testid="wealth-profile-review_frequency"
                      onChange={(e) => setProfile({ ...profile, review_frequency: e.target.value })}
                      className="mt-2 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-navy outline-none focus:border-plum disabled:opacity-60">
                {["Quarterly", "Half-yearly", "Annual", "Custom"].map((f) => <option key={f}>{f}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="label-caps">Wealth purpose statement</span>
            <textarea rows={4} value={profile.wealth_purpose || ""} disabled={!canEdit}
                      data-testid="wealth-profile-purpose"
                      onChange={(e) => setProfile({ ...profile, wealth_purpose: e.target.value })}
                      className="mt-2 w-full resize-y rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-plum disabled:opacity-60" />
          </label>
          {canEdit && (
            <button onClick={save} data-testid="wealth-profile-save"
                    className="rounded-full bg-plum px-6 py-3 text-[10px] uppercase tracking-[0.16em] text-white hover:bg-plum/90">
              Save profile
            </button>
          )}
        </div>

        <div className="panel overflow-hidden" data-testid="wealth-entities">
          <p className="border-b border-border bg-navy/5 px-5 py-3 font-display text-lg text-navy">
            Ownership entities
          </p>
          <div className="divide-y divide-border">
            {entities.length === 0 && <p className="px-5 py-4 text-sm text-muted-foreground">No entities yet.</p>}
            {entities.map((en) => (
              <div key={en.id} className="flex items-center justify-between gap-3 px-5 py-3"
                   data-testid={`wealth-entity-${en.id}`}>
                <div>
                  <p className="text-sm font-medium text-navy">{en.name}</p>
                  <p className="label-caps mt-1">{en.entity_type}</p>
                </div>
                {canEdit && (
                  <button onClick={() => removeEntity(en.id)} data-testid={`wealth-entity-delete-${en.id}`}
                          className="rounded-full p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                    <Trash size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
          {canEdit && (
            <div className="space-y-3 border-t border-border bg-muted/30 p-5">
              <p className="label-caps">Add an entity</p>
              <input placeholder="Name (e.g. Ramanathan Family Trust)" value={form.name}
                     data-testid="wealth-entity-name"
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-plum" />
              <select value={form.entity_type} data-testid="wealth-entity-type"
                      onChange={(e) => setForm({ ...form, entity_type: e.target.value })}
                      className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-plum">
                {(config?.ownership_forms?.items || ["Individual"]).map((f) => <option key={f}>{f}</option>)}
              </select>
              <button onClick={addEntity} data-testid="wealth-entity-add"
                      className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-[10px] uppercase tracking-[0.16em] text-white hover:bg-navy/90">
                <Plus size={13} /> Add entity
              </button>
            </div>
          )}
        </div>
      </div>
    </WealthShell>
  );
}
